import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { getBrandAsset } from '@/themes';
import { cn } from '@/lib/utils';
import { EXPRESS_STAGES, EXPRESS_STEPS, SEND_STEP_ORDER, type ExpressStepKey } from './expressSteps';
import { CampaignInvitedBy } from './InvitedBy';

const HEADLINE =
  'text-[clamp(1.75rem,1.15rem+1.7vw,2.5rem)] font-medium leading-tight tracking-[-0.024em] text-foreground [text-wrap:pretty] focus:outline-none';
const EYEBROW = 'text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground';

/** Room between the page's last line and the order bar once it has scrolled to the end. */
const GAP_ABOVE_BAR_PX = 24;

/**
 * The order bar's height as rendered, so the page can pad itself by it. The bar grows with the
 * lines it carries (a bought list adds two), so a fixed padding leaves the last field under it.
 */
function useMeasuredHeight<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, height };
}

export interface ExpressStepProps {
  step: ExpressStepKey;
  /** Replaces the step's own eyebrow, headline or subhead, e.g. for a signed-in person. */
  label?: string;
  title?: string;
  sub?: string | null;
  children: ReactNode;
  /** Shown beside the step from tablet width up, such as a preview of the text. */
  aside?: ReactNode;
  /**
   * A large preview on a tinted panel beside the step from tablet width up, in a wider page. Replaces
   * `aside` where the preview is the point of the screen.
   */
  preview?: ReactNode;
  /** Who sent the Express link, beside the logo. A send step without it names the selected campaign's Consultant. */
  invitedBy?: ReactNode;
  error?: ReactNode;
  onBack?: () => void;
  /** The order bar's main button; it submits the step, so Enter works from any field. */
  primary?: {
    label: string;
    disabled?: boolean;
    /** Why the button is held, shown beside it while it is. */
    heldBecause?: string | null;
    busy?: boolean;
    busyLabel?: string;
    onPress: () => void;
  };
  /** The order bar's running total, beside its button. */
  summary?: ReactNode;
  /** The order bar's priced lines, on their own row above the total so they read on a phone. */
  lines?: ReactNode;
}

/**
 * One Express screen: a large question, its fields settling in, and the translucent order bar the page
 * scrolls under, as on the voter list builder. Full screen, outside the app's sidebar and header.
 */
export function ExpressStep({
  step,
  label,
  title,
  sub,
  children,
  aside,
  preview,
  invitedBy,
  error,
  onBack,
  primary,
  summary,
  lines,
}: ExpressStepProps) {
  const copy = EXPRESS_STEPS[step];
  const stage = EXPRESS_STAGES.indexOf(copy.stage);
  const headline = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);
  const bar = useMeasuredHeight<HTMLElement>();

  // Move focus to the new question so a screen reader announces it; not on first load, where the first field keeps it.
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    headline.current?.focus();
  }, [step]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (primary && !primary.disabled && !primary.busy) primary.onPress();
  };
  const subhead = sub === undefined ? copy.sub : sub;
  const width = preview ? 'max-w-6xl' : 'max-w-4xl';

  return (
    <MotionConfig reducedMotion="user">
      <form
        onSubmit={submit}
        noValidate
        className="min-h-screen bg-background pb-40"
        style={bar.height === null ? undefined : { paddingBottom: bar.height + GAP_ABOVE_BAR_PX }}
      >
        <header className={cn('mx-auto px-4 pt-6 sm:px-6', width)}>
          <div className="flex items-center justify-between gap-4">
            <span className="shrink-0 rounded-lg bg-logo-surface px-3 py-2">
              <img src={getBrandAsset('logo.svg')} alt="Logo" className="h-6" />
            </span>
            {invitedBy ?? ((SEND_STEP_ORDER as readonly ExpressStepKey[]).includes(step) && <CampaignInvitedBy />)}
          </div>
          <StageProgress stage={stage} />
        </header>

        <main className={cn('mx-auto px-4 sm:px-6', width)}>
          <motion.section
            key={step}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'grid gap-12 py-8 sm:py-12',
              preview ? 'md:grid-cols-[minmax(0,1fr)_minmax(340px,420px)] lg:gap-16' : aside && 'md:grid-cols-[1fr_280px]',
            )}
          >
            <div className="min-w-0 max-w-xl">
              <p data-testid="express-step-label" className={EYEBROW}>
                {label ?? copy.label}
              </p>
              <h1 ref={headline} tabIndex={-1} className={cn(HEADLINE, 'mt-3')}>
                {title ?? copy.title}
              </h1>
              {subhead && <p className="mt-3 text-[1.0625rem] text-muted-foreground">{subhead}</p>}
              <div className="mt-8">{children}</div>
              {error && (
                <div role="alert" className="mt-6 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
            {preview ? (
              <div className="hidden md:block">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className="sticky top-6 rounded-3xl bg-primary/5 px-6 py-8"
                >
                  {preview}
                </motion.div>
              </div>
            ) : (
              aside && <div className="hidden md:block">{aside}</div>
            )}
          </motion.section>
        </main>

        <section ref={bar.ref} aria-label="Your order" className="voter-glass fixed inset-x-0 bottom-0 z-40 border-t">
          <div className={cn('mx-auto px-4 pb-[max(14px,env(safe-area-inset-bottom))] pt-3 sm:px-6', width)}>
            {lines && <div className="mb-2">{lines}</div>}
            {primary?.disabled && primary.heldBecause && (
              <p id="express-primary-held" role="status" className="mb-2 text-right text-sm text-muted-foreground">
                {primary.heldBecause}
              </p>
            )}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    Back
                  </button>
                )}
              </div>
              <div className="ml-auto flex min-w-0 items-center gap-3 sm:gap-4">
                {summary}
                {primary && (
                  <Button
                    type="submit"
                    size="lg"
                    disabled={primary.disabled || primary.busy}
                    aria-describedby={primary.disabled && primary.heldBecause ? 'express-primary-held' : undefined}
                  >
                    {primary.busy ? (
                      <>
                        <Loader2 className="animate-spin" aria-hidden="true" />
                        {primary.busyLabel ?? primary.label}
                      </>
                    ) : (
                      primary.label
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>
      </form>
    </MotionConfig>
  );
}

/**
 * Where the person is, as a bar of one segment per stage. Every stage keeps its name for screen readers;
 * on phones only the current one is written out.
 */
function StageProgress({ stage }: { stage: number }) {
  return (
    <div className="mt-5">
      <p aria-hidden="true" className="mb-1.5 text-xs font-semibold text-foreground sm:hidden">
        {EXPRESS_STAGES[stage]}
      </p>
      <ol aria-label="Stages" className="grid grid-cols-6 gap-1.5">
        {EXPRESS_STAGES.map((name, k) => (
          <li key={name} aria-current={k === stage ? 'step' : undefined} className="min-w-0">
            <span aria-hidden="true" className={cn('block h-1 rounded-full', k <= stage ? 'bg-primary' : 'bg-border')} />
            <span
              className={cn(
                'mt-1.5 block truncate text-xs max-sm:sr-only',
                k === stage ? 'font-semibold text-foreground' : 'text-muted-foreground',
              )}
            >
              {name}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
